<template>
  <div class="ai-dashboard-builder">
    <theHeader />
    <div class="content-wrapper container py-5">
      <!-- Success Modal -->
      <b-modal id="success-modal" title="Dashboard Created Successfully" hide-footer centered>
        <div class="text-center p-3">
          <div class="success-icon mb-3">✓</div>
          <h4>Congratulations!</h4>
          <p class="text-muted">
            Your global dashboard <strong>{{ dashboardName }}</strong> has been configured with AI and is ready.
          </p>
          <b-button variant="success" class="mt-3 px-4" @click="$bvModal.hide('success-modal')">
            Close
          </b-button>
        </div>
      </b-modal>

      <!-- Page Header -->
      <div class="row mb-5">
        <div class="col text-center">
          <span class="badge badge-soft-primary mb-2">AI-Powered Feature</span>
          <h1 class="display-5 font-weight-bold text-dark">AI Dashboard Builder</h1>
          <p class="lead text-muted mx-auto" style="max-width: 600px;">
            Input your requirements and let our AI curate the ideal indicators and data sources to construct your custom health services dashboard.
          </p>
        </div>
      </div>

      <!-- Main Panel -->
      <div class="row justify-content-center">
        <div class="col-lg-8">

          <!-- Generator Form Card -->
          <div v-if="!showPreview" class="card form-card shadow-lg border-0">
            <div class="card-body p-4 p-md-5">

              <!-- Dashboard Name -->
              <b-form-group label="Dashboard Name" label-for="dashName" class="mb-4">
                <template #label>
                  <span class="form-label-custom">Dashboard Name <span class="text-danger">*</span></span>
                </template>
                <b-form-input
                  id="dashName"
                  v-model="dashboardName"
                  placeholder="e.g. Health Service Coverage Dashboard"
                  class="custom-input"
                  required
                ></b-form-input>
              </b-form-group>

              <!-- Dashboard Description -->
              <b-form-group label="Dashboard Description" label-for="dashDesc" class="mb-4">
                <template #label>
                  <span class="form-label-custom">Dashboard Description <span class="text-danger">*</span></span>
                </template>
                <b-form-textarea
                  id="dashDesc"
                  v-model="dashboardDescription"
                  placeholder="Describe the goals, targets, or scope of the dashboard. The AI uses this description to suggest relevant indicators."
                  rows="4"
                  class="custom-input"
                  required
                ></b-form-textarea>
              </b-form-group>

              <!-- Curation Mode Selection -->
              <b-form-group class="mb-4">
                <template #label>
                  <span class="form-label-custom">Curation Mode <span class="text-danger">*</span></span>
                </template>

                <div class="row">
                  <div class="col-md-6 mb-3 mb-md-0">
                    <div
                      class="strategy-option"
                      :class="{ active: curationMode === 'automated' }"
                      @click="curationMode = 'automated'"
                    >
                      <div class="strategy-icon">⚡</div>
                      <div class="strategy-details">
                        <h5>Fully Automated AI Curation</h5>
                        <p>Let AI select the best indicators automatically using the description.</p>
                      </div>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div
                      class="strategy-option"
                      :class="{ active: curationMode === 'manual' }"
                      @click="curationMode = 'manual'"
                    >
                      <div class="strategy-icon">🛠️</div>
                      <div class="strategy-details">
                        <h5>Manual Selection Constraints</h5>
                        <p>Manually specify program areas or tags to restrict the dashboard data.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </b-form-group>

              <!-- Manual Selection Subsection -->
              <transition name="fade-slide" mode="out-in">
                <div v-if="curationMode === 'manual'" key="manual-section" class="manual-selection-section p-4 rounded border mb-4 bg-light">
                  <!-- Strategy Selection -->
                  <b-form-group class="mb-4">
                    <template #label>
                      <span class="form-label-custom">Indicator Selection Strategy <span class="text-danger">*</span></span>
                    </template>

                    <div class="row">
                      <div class="col-md-6 mb-3 mb-md-0">
                        <div
                          class="strategy-option"
                          :class="{ active: selectionStrategy === 'program_area' }"
                          @click="selectionStrategy = 'program_area'"
                        >
                          <div class="strategy-icon">📂</div>
                          <div class="strategy-details">
                            <h5>Program Area Selection</h5>
                            <p>Limit AI search to specific program areas.</p>
                          </div>
                        </div>
                      </div>

                      <div class="col-md-6">
                        <div
                          class="strategy-option"
                          :class="{ active: selectionStrategy === 'tags' }"
                          @click="selectionStrategy = 'tags'"
                        >
                          <div class="strategy-icon">🏷️</div>
                          <div class="strategy-details">
                            <h5>Overlapping Tag Selection</h5>
                            <p>Allow AI to select indicators based on overlapping tag combinations.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-form-group>

                  <!-- Dynamic Strategy Selection Fields -->
                  <transition name="fade-slide" mode="out-in">
                    <!-- Program Area Selector -->
                    <div v-if="selectionStrategy === 'program_area'" key="program_area" class="mb-0">
                      <b-form-group class="mb-0">
                        <template #label>
                          <span class="form-label-custom">Select Program Areas <span class="text-danger">*</span></span>
                        </template>

                        <multiselect
                          v-model="selectedProgramAreas"
                          :options="programAreas"
                          :multiple="true"
                          :close-on-select="false"
                          placeholder="Search and select Program Areas..."
                          class="custom-multiselect"
                        >
                        </multiselect>
                        <small class="text-muted mt-1 d-block">
                          The AI will only filter indicators that fall within these selected program areas.
                        </small>
                      </b-form-group>
                    </div>

                    <!-- Overlapping Tag Selector -->
                    <div v-else-if="selectionStrategy === 'tags'" key="tags" class="mb-0">
                      <b-form-group class="mb-0">
                        <template #label>
                          <span class="form-label-custom">Select Indicator Tags <span class="text-danger">*</span></span>
                        </template>

                        <!-- Selected tags container (pills/badges that can be removed) -->
                        <div v-if="selectedTags.length > 0" class="selected-tags-pills mb-3 p-3 bg-light rounded border">
                          <span class="text-muted font-size-xs text-uppercase font-weight-bold d-block mb-2">Selected Tags:</span>
                          <div class="d-flex flex-wrap">
                            <span
                              v-for="tag in selectedTags"
                              :key="tag.id"
                              class="badge badge-tag-pill mr-2 mb-2"
                              @click="toggleTag(tag)"
                            >
                              {{ tag.name }} <span class="remove-pill">&times;</span>
                            </span>
                          </div>
                        </div>

                        <!-- Search box -->
                        <div class="search-box mb-3">
                          <b-form-input
                            v-model="tagSearchTerm"
                            placeholder="Filter tags by name..."
                            class="custom-input search-input"
                          ></b-form-input>
                        </div>

                        <!-- Scrollable button container -->
                        <div class="tags-buttons-container p-3 border rounded">
                          <div v-if="filteredTagsList.length === 0" class="text-center py-3 text-muted">
                            No matching tags found.
                          </div>
                          <div v-else class="d-flex flex-wrap">
                            <button
                              v-for="tag in filteredTagsList"
                              :key="tag.id"
                              type="button"
                              class="btn btn-sm btn-tag-selectable mr-2 mb-2"
                              :class="{ active: isTagSelected(tag) }"
                              @click="toggleTag(tag)"
                            >
                              <span v-if="isTagSelected(tag)" class="mr-1">✓</span>
                              {{ tag.name }}
                            </button>
                          </div>
                        </div>
                        <small class="text-muted mt-2 d-block">
                          Select tags from the list above. The AI will look for indicators matching these tags.
                        </small>
                      </b-form-group>
                    </div>
                  </transition>
                </div>
              </transition>

              <!-- Webhook URL Configuration Warning -->
              <div v-if="!webhookUrl" class="alert alert-soft-warning border-0 p-3 mb-4 d-flex align-items-center">
                <div class="mr-3 font-size-lg">ℹ️</div>
                <div class="small">
                  Note: Webhook URL is not set. Clicking submit will trigger a high-fidelity simulated AI curation workflow.
                </div>
              </div>

              <!-- Submit Button -->
              <b-button
                block
                size="lg"
                class="btn-generate mt-4 py-3"
                :disabled="!isFormValid || loadingResponse"
                @click="generateDashboard"
              >
                <span v-if="loadingResponse">
                  <b-spinner small class="mr-2"></b-spinner> Running AI Workflow...
                </span>
                <span v-else>
                  ✨ Generate Global Dashboard with AI
                </span>
              </b-button>

            </div>
          </div>

          <!-- Loading Animation View -->
          <div v-if="loadingResponse" class="card loading-card shadow-lg border-0 text-center p-5 mb-4">
            <div class="card-body">
              <div class="loader-visual mb-4">
                <div class="pulse-ring"></div>
                <span class="ai-sparkle">🤖</span>
              </div>
              <h3 class="font-weight-bold text-dark">{{ activeLoadingStep }}</h3>
              <p class="text-muted">{{ activeLoadingSubtext }}</p>

              <!-- Progress Indicator dots -->
              <div class="loading-steps-list text-left mx-auto mt-4" style="max-width: 400px;">
                <div
                  v-for="(step, idx) in loadingSteps"
                  :key="idx"
                  class="loading-step-item d-flex align-items-center py-2"
                  :class="{
                    completed: idx < currentStepIndex,
                    active: idx === currentStepIndex,
                    pending: idx > currentStepIndex
                  }"
                >
                  <div class="step-check mr-3">
                    <span v-if="idx < currentStepIndex">✓</span>
                    <b-spinner v-else-if="idx === currentStepIndex" small></b-spinner>
                    <span v-else>•</span>
                  </div>
                  <span class="step-text">{{ step.title }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Preview Dashboard Card -->
          <transition name="fade">
            <div v-if="showPreview && !loadingResponse" class="card preview-card shadow-lg border-0 mb-5">
              <div class="card-header bg-primary text-white p-4 d-flex justify-content-between align-items-center">
                <div>
                  <h4 class="mb-1 font-weight-bold">✨ AI Curated Dashboard Preview</h4>
                  <p class="mb-0 text-white-50 small">Verify the proposed dashboard structure before committing.</p>
                </div>
                <b-button variant="outline-light" size="sm" @click="resetForm">
                  Start Over
                </b-button>
              </div>
              <div class="card-body p-4 p-md-5">
                <!-- Details -->
                <div class="row mb-4">
                  <div class="col-md-6">
                    <span class="text-muted font-size-xs text-uppercase font-weight-bold d-block mb-1">Dashboard Name</span>
                    <h4 class="font-weight-bold text-dark">{{ dashboardName }}</h4>
                  </div>
                  <div class="col-md-6">
                    <span class="text-muted font-size-xs text-uppercase font-weight-bold d-block mb-1">Curation Strategy</span>
                    <b-badge variant="info" class="px-2 py-1">
                      <span v-if="curationMode === 'automated'">Fully Automated Curation</span>
                      <span v-else-if="selectionStrategy === 'program_area'">Manual Selection: Program Areas</span>
                      <span v-else-if="selectionStrategy === 'tags'">Manual Selection: Overlapping Tags</span>
                    </b-badge>
                  </div>
                </div>

                <div class="row mb-4">
                  <div class="col-12">
                    <span class="text-muted font-size-xs text-uppercase font-weight-bold d-block mb-1">Goal / Description</span>
                    <p class="text-dark bg-light p-3 rounded" style="border-left: 4px solid #007d53;">
                      {{ dashboardDescription }}
                    </p>
                  </div>
                </div>

                <hr class="my-4">

                <!-- Curated Indicators List -->
                <div class="mb-4">
                  <h5 class="font-weight-bold text-dark d-flex align-items-center mb-3">
                    <span class="mr-2">📊</span> Curated Indicators
                    <b-badge variant="success" class="ml-2">{{ curatedIndicators.length }} Selected</b-badge>
                  </h5>
                  <div class="table-responsive">
                    <table class="table table-hover align-middle">
                      <thead class="bg-light">
                        <tr>
                          <th>Indicator Name</th>
                          <th>Program Area</th>
                          <th>Match Reason</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(indicator, idx) in curatedIndicators" :key="idx">
                          <td class="font-weight-bold text-dark">{{ indicator.name }}</td>
                          <td>
                            <b-badge variant="secondary" class="badge-soft">{{ indicator.programArea }}</b-badge>
                          </td>
                          <td class="small text-muted">{{ indicator.reason }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Curated Data Sources List -->
                <div class="mb-4">
                  <h5 class="font-weight-bold text-dark d-flex align-items-center mb-3">
                    <span class="mr-2">🔌</span> Curated Data Sources
                    <b-badge variant="info" class="ml-2">{{ curatedDataSources.length }} Selected</b-badge>
                  </h5>
                  <div class="d-flex flex-wrap gap-2">
                    <span
                      v-for="(source, idx) in curatedDataSources"
                      :key="idx"
                      class="data-source-tag mr-2 mb-2"
                    >
                      <span class="bullet"></span> {{ source }}
                    </span>
                  </div>
                </div>

                <!-- AI Recommendation Explainer -->
                <div class="alert alert-soft-success border-0 p-4 mb-4">
                  <h6 class="font-weight-bold text-success mb-2">💡 AI Recommendation Summary</h6>
                  <p class="mb-0 text-dark small" style="line-height: 1.6;">
                    {{ aiSummary }}
                  </p>
                </div>

                <!-- Preview Actions -->
                <div class="d-flex gap-3 justify-content-end mt-4">
                  <b-button variant="secondary" class="px-4 mr-2" @click="showPreview = false">
                    Back to Edit
                  </b-button>
                  <b-button variant="success" class="px-5 font-weight-bold btn-deploy" @click="deployDashboard">
                    Confirm and Build Dashboard
                  </b-button>
                </div>
              </div>
            </div>
          </transition>

        </div>
      </div>
    </div>
    <theFooter />
  </div>
</template>

<script>
import apiServices from '@/modules/data-layer/services/ApiServices';
import axios from 'axios';
import theHeader from '../about/layout/theHeader.vue';
import theFooter from '../about/layout/theFooter.vue';

const globalTags = [
  'child health', 'maternal health', 'reproductive maternal newborn and child health',
  'reproductive health', 'family planning', 'neonatal', 'women', 'men', 'youth',
  'demographics', 'mortality', 'infectious diseases', 'malaria', 'hiv', 'tuberculosis',
  'sexually transmitted infections', 'covid-19', 'monkeypox', 'lassa fever', 'cholera',
  'measles', 'yellow fever', 'meningitis', 'diphtheria', 'tetanus', 'polio',
  'neglected tropical diseases', 'hepatitis', 'acute respiratory infections',
  'non-communicable diseases', 'cardiovascular', 'mental health', 'diabetes',
  'cancer', 'injuries and trauma', 'road traffic accidents', 'substance abuse',
  'nutrition', 'stunting', 'wasting', 'breastfeeding', 'exclusive breastfeeding',
  'micronutrients', 'vitamin a', 'surveillance', 'risk behaviour', 'health financing',
  'expenditure', 'budget', 'revenue', 'finance', 'out-of-pocket payments',
  'health insurance', 'economy', 'socioeconomic', 'equity', 'household income',
  'human resources for health', 'workforce', 'personnel', 'migration',
  'doctors, nurses and midwives', 'community health workers', 'dental', 'pharmacists',
  'laboratory', 'radiographers', 'rehabilitation', 'optometry', 'public analysts',
  'environmental health practitioners', 'infrastructure', 'primary health care',
  'secondary health care', 'tertiary health care', 'facility management',
  'quality of care', 'service delivery', 'access', 'antenatal care', 'postnatal care',
  'skilled birth attendance', 'insecticide-treated nets',
  'prevention of mother-to-child transmission', 'drugs and commodities',
  'equipment', 'supply chain', 'blood safety', 'referral systems',
  'emergency medicine', 'ambulance', 'documentation', 'data quality', 'testing',
  'immunization', 'targets', 'water', 'sanitation', 'hygiene', 'environment',
  'climate', 'housing', 'education', 'schools', 'gender-based violence',
  'agriculture', 'food security', 'media', 'technology', 'household characteristics',
  'labour and employment', 'disability', 'elderly', 'aging',
  'orphans and vulnerable children',
];

export default {
  name: 'AiDashboardBuilder',
  components: {
    theHeader,
    theFooter,
  },
  data() {
    return {
      // Configuration Variable - replace this with n8n webhook URL when provided
      webhookUrl: '',

      // Form inputs
      dashboardName: '',
      dashboardDescription: '',
      curationMode: 'automated', // 'automated' or 'manual'
      selectionStrategy: 'program_area', // 'program_area' or 'tags'

      // Dynamic selections lists
      selectedProgramAreas: [],
      selectedTags: [],

      // Tag filter search input
      tagSearchTerm: '',

      // API-fetched datasets
      programAreas: [],
      tags: [],
      loadingTags: false,

      // State UI
      loadingResponse: false,
      showPreview: false,

      // AI workflow loading step simulation
      currentStepIndex: 0,
      loadingSteps: [
        { title: 'Validating workflow payload & settings...', subtext: 'Verifying inputs and parameters.' },
        { title: 'Connecting to n8n AI webhook...', subtext: 'Sending POST request to automation workflow.' },
        { title: 'Analyzing database indicator tagging metadata...', subtext: 'Matching parameters with indicator tables.' },
        { title: 'Curating program area child mappings...', subtext: 'Fetching relevant data sources and metrics.' },
        { title: 'Generating preview recommendations...', subtext: 'Crystallizing preference schema.' },
      ],
      stepTimer: null,

      // Curated results from webhook/mock workflow
      curatedIndicators: [],
      curatedDataSources: [],
      aiSummary: '',
    };
  },
  computed: {
    isFormValid() {
      if (!this.dashboardName.trim() || !this.dashboardDescription.trim()) {
        return false;
      }
      if (this.curationMode === 'manual') {
        if (this.selectionStrategy === 'program_area' && this.selectedProgramAreas.length === 0) {
          return false;
        }
        if (this.selectionStrategy === 'tags' && this.selectedTags.length === 0) {
          return false;
        }
      }
      return true;
    },
    activeLoadingStep() {
      if (this.currentStepIndex < this.loadingSteps.length) {
        return this.loadingSteps[this.currentStepIndex].title;
      }
      return 'Processing...';
    },
    activeLoadingSubtext() {
      if (this.currentStepIndex < this.loadingSteps.length) {
        return this.loadingSteps[this.currentStepIndex].subtext;
      }
      return 'Please wait...';
    },
    filteredTagsList() {
      const search = this.tagSearchTerm.trim().toLowerCase();
      if (!search) return this.tags;
      return this.tags.filter((tag) => tag.name.toLowerCase().includes(search));
    },
  },
  watch: {
    selectionStrategy(newStrategy) {
      if (newStrategy === 'tags' && this.tags.length === 0) {
        this.loadTags();
      }
    },
  },
  created() {
    this.loadProgramAreas();
    this.initGlobalTags();
  },
  methods: {
    initGlobalTags() {
      this.tags = globalTags.map((tag, idx) => ({
        id: idx.toString(),
        name: tag,
        description: '',
      }));
    },
    async loadProgramAreas() {
      try {
        // Dispatch store request to ensure indicators and program areas are loaded
        await this.$store.dispatch('loadIndicators');
        const programAreaList = this.$store.getters.getprogramArea || [];
        this.programAreas = programAreaList.map((section) => section.parent.value);
      } catch (err) {
        console.error('Error loading program areas:', err);
      }
    },
    async loadTags() {
      // Tags are pre-populated using globalTags list initialized in created()
      // Optional: fetch and merge backend tags if needed
      this.loadingTags = true;
      try {
        const { data } = await apiServices.getTags();
        if (data && data.data && data.data.results) {
          const fetchedTags = data.data.results.map((tag) => ({
            id: tag.id.toString(),
            name: tag.name,
            description: tag.description,
          }));

          // Merge without duplicates based on name
          const merged = [...this.tags];
          fetchedTags.forEach((ft) => {
            if (!merged.some((t) => t.name.toLowerCase() === ft.name.toLowerCase())) {
              merged.push(ft);
            }
          });
          this.tags = merged;
        }
      } catch (err) {
        console.error('Error loading backend tags:', err);
      } finally {
        this.loadingTags = false;
      }
    },
    isTagSelected(tag) {
      return this.selectedTags.some((t) => t.name === tag.name);
    },
    toggleTag(tag) {
      const idx = this.selectedTags.findIndex((t) => t.name === tag.name);
      if (idx > -1) {
        this.selectedTags.splice(idx, 1);
      } else {
        this.selectedTags.push(tag);
      }
    },
    resetForm() {
      this.dashboardName = '';
      this.dashboardDescription = '';
      this.curationMode = 'automated';
      this.selectedProgramAreas = [];
      this.selectedTags = [];
      this.tagSearchTerm = '';
      this.showPreview = false;
    },
    async generateDashboard() {
      this.loadingResponse = true;
      this.currentStepIndex = 0;

      // Start stepping timer (simulates workflow transitions)
      this.stepTimer = setInterval(() => {
        if (this.currentStepIndex < this.loadingSteps.length - 1) {
          this.currentStepIndex += 1;
        }
      }, 900);

      // Prepare Payload for n8n Webhook
      const payload = {
        name: this.dashboardName,
        description: this.dashboardDescription,
        curation_mode: this.curationMode,
        strategy: this.curationMode === 'manual' ? this.selectionStrategy : 'automated',
        selected_program_areas: this.curationMode === 'manual' && this.selectionStrategy === 'program_area' ? this.selectedProgramAreas : [],
        selected_tags: this.curationMode === 'manual' && this.selectionStrategy === 'tags' ? this.selectedTags.map((tag) => tag.name) : [],
      };

      console.log('Sending payload to n8n Webhook:', payload);

      try {
        let resultsData = null;

        if (this.webhookUrl) {
          // Trigger the n8n POST request
          const response = await axios.post(this.webhookUrl, payload);
          resultsData = response.data;
        } else {
          // Simulate the webhook API call wait delay
          await new Promise((resolve) => setTimeout(resolve, 4500));

          // High-fidelity Mock Response (adopting tags or program area criteria)
          resultsData = this.generateMockResponse(payload);
        }

        // Populates indicators & data sources curation
        this.curatedIndicators = resultsData.indicators || [];
        this.curatedDataSources = resultsData.data_sources || [];
        this.aiSummary = resultsData.ai_summary || '';

        // Complete loading
        clearInterval(this.stepTimer);
        this.currentStepIndex = this.loadingSteps.length;
        this.loadingResponse = false;
        this.showPreview = true;
      } catch (error) {
        console.error('Error generating AI recommendations:', error);
        clearInterval(this.stepTimer);
        this.loadingResponse = false;

        this.$swal({
          icon: 'error',
          title: 'AI Curation Failed',
          text: error.message || 'An error occurred triggering the AI n8n workflow.',
          confirmButtonColor: '#007d53',
        });
      }
    },
    generateMockResponse(payload) {
      // Create high quality mock recommendations based on input values
      const keyword = `${payload.name.toLowerCase()} ${payload.description.toLowerCase()}`;

      let indicators = [];
      let dataSources = [];
      let summary = '';

      if (payload.curation_mode === 'manual') {
        if (payload.strategy === 'program_area') {
          const areas = payload.selected_program_areas;
          indicators = areas.map((area) => ({
            name: `${area} Core Tracer Indicator`,
            programArea: area,
            reason: `Direct manual selection matches the specified program area: ${area}.`,
          }));
          if (indicators.length === 0) {
            indicators = [{ name: 'Default Manual Indicator', programArea: 'General', reason: 'No program areas chosen.' }];
          }
          dataSources = ['NHMIS (monthly)', 'National Data Repository'];
          summary = `This dashboard has been manually restricted to indicators in the program areas: ${areas.join(', ')}. The curation aligns precisely with your manual parameters.`;
        } else if (payload.strategy === 'tags') {
          const tags = payload.selected_tags;
          indicators = tags.map((tag) => ({
            name: `${tag.charAt(0).toUpperCase() + tag.slice(1)} Coverage Index`,
            programArea: 'Multiple',
            reason: `Direct manual selection matches the tagged index: ${tag}.`,
          }));
          if (indicators.length === 0) {
            indicators = [{ name: 'Default Tagged Indicator', programArea: 'General', reason: 'No tags chosen.' }];
          }
          dataSources = ['DHIS2 Platform', 'Household Surveys'];
          summary = `This dashboard was manually curated based on overlapping tags: ${tags.join(', ')}. Only indicators mapped to these specific tag attributes are included.`;
        }
      } else if (keyword.includes('child') || keyword.includes('immuniz') || keyword.includes('penta')) {
        indicators = [
          { name: 'Penta 3 Coverage', programArea: 'RMNCH', reason: 'High relevance for tracking routine immunization uptake.' },
          { name: 'Fully Immunized Children', programArea: 'RMNCH', reason: 'Primary outcome metric for child immunization status.' },
          { name: 'BCG Coverage', programArea: 'RMNCH', reason: 'Tracks neonatal vaccine access.' },
          { name: 'Diarrhea Treatment with ORS & Zinc', programArea: 'RMNCH', reason: 'Essential indicator for pediatric care quality.' },
        ];
        dataSources = ['NHMIS (monthly)', 'NDHS (Survey)', 'MICS (Survey)'];
        summary = `This child health immunization dashboard has been curated based on the "${payload.name}" configuration. It brings together routine facility metrics (from NHMIS) with household surveys (NDHS and MICS) to highlight immunization coverage and assess gaps in access. The chosen indicators focus primarily on early immunization completions and standard disease treatment rates.`;
      } else if (keyword.includes('maternal') || keyword.includes('anc') || keyword.includes('deliver') || keyword.includes('preg')) {
        indicators = [
          { name: 'ANC 4+ Coverage', programArea: 'RMNCH', reason: 'Tracks completion of antenatal care checkups.' },
          { name: 'Skilled Birth Attendant Delivery', programArea: 'RMNCH', reason: 'Crucial for assessing safe delivery practices.' },
          { name: 'Institutional Delivery Rate', programArea: 'RMNCH', reason: 'Measures hospital birth versus community birth.' },
          { name: 'Maternal Mortality Ratio', programArea: 'Maternal Death Assessment', reason: 'Measures ultimate maternal safety outcomes.' },
        ];
        dataSources = ['NHMIS (monthly)', 'NDHS (Survey)', 'MPCDR Repository'];
        summary = 'This maternal health service coverage dashboard aligns with the goal of tracking pregnancy and delivery safety. Triangulating routine clinic data from NHMIS with survey records from NDHS allows for real-time tracking of antenatal compliance alongside comprehensive audit logs from the Maternal Perinatal and Child Death Review (MPCDR).';
      } else {
        // Fallback default mock
        indicators = [
          { name: 'Antenatal Care 1st Visit Coverage', programArea: 'RMNCH', reason: 'Key entry point for maternal health service uptake.' },
          { name: 'Penta 3 Coverage', programArea: 'RMNCH', reason: 'Reliable tracer indicator for child immunization continuity.' },
          { name: 'Modern Contraceptive Prevalence Rate', programArea: 'Family Planning', reason: 'Crucial metric for family planning services.' },
          { name: 'Out-Of-Pocket Health Expenditures', programArea: 'Health Financing', reason: 'Measures financial protection structures.' },
        ];
        dataSources = ['NHMIS (monthly)', 'NDHS (Survey)', 'NHA Reports'];
        summary = `Based on your description ("${payload.description.substring(0, 80)}..."), the AI has constructed a balanced health service coverage dashboard. It covers primary service uptake pathways across RMNCH and Family Planning, while including a Health Financing dimension to analyze the affordability of access. Routine health service data is cross-referenced with national surveys for triangulation.`;
      }
      return {
        indicators,
        data_sources: dataSources,
        ai_summary: summary,
      };
    },
    deployDashboard() {
      // Simulate backend save/deploy
      this.$bvModal.show('success-modal');
      this.resetForm();
    },
  },
};
</script>

<style scoped>
/* Page Layout */
.ai-dashboard-builder {
  background-color: #f7f9fa;
  min-height: 100vh;
}

.content-wrapper {
  margin-top: 20px;
  margin-bottom: 60px;
}

/* Badge styles */
.badge-soft-primary {
  background-color: rgba(0, 125, 83, 0.1);
  color: #007d53;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 6px 12px;
  border-radius: 50px;
}

/* Cards */
.card {
  border-radius: 12px;
  overflow: hidden;
}

.form-card {
  background-color: #ffffff;
  border: 1px solid #eef2f4;
}

.form-label-custom {
  font-family: 'Work Sans', sans-serif;
  font-weight: 600;
  color: #2b303a;
  font-size: 0.95rem;
}

/* Custom Inputs */
.custom-input {
  border: 1px solid #ced4da;
  border-radius: 8px;
  padding: 10px 14px;
  font-family: 'Work Sans', sans-serif;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.custom-input:focus {
  border-color: #007d53;
  box-shadow: 0 0 0 3px rgba(0, 125, 83, 0.15);
}

/* Strategy Selectors */
.strategy-option {
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  background-color: #ffffff;
  transition: all 0.25s ease;
  height: 100%;
}

.strategy-option:hover {
  border-color: #9cd5c4;
  background-color: #fafdfc;
}

.strategy-option.active {
  border-color: #007d53;
  background-color: #f0fdf4;
}

.strategy-icon {
  font-size: 1.8rem;
  margin-right: 12px;
  line-height: 1;
}

.strategy-details h5 {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 4px;
  font-family: 'Work Sans', sans-serif;
}

.strategy-details p {
  font-size: 0.8rem;
  color: #718096;
  margin-bottom: 0;
  line-height: 1.4;
}

/* Generate Button */
.btn-generate {
  background-color: #007d53;
  border-color: #007d53;
  font-family: 'Work Sans', sans-serif;
  font-weight: 700;
  font-size: 1.05rem;
  border-radius: 8px;
  color: #ffffff;
  transition: all 0.2s ease;
}

.btn-generate:hover:not(:disabled),
.btn-generate:focus:not(:disabled) {
  background-color: #00603f;
  border-color: #00603f;
  transform: translateY(-1px);
}

.btn-generate:disabled {
  background-color: #a0aec0;
  border-color: #a0aec0;
}

/* Loading Card */
.loading-card {
  background-color: #ffffff;
}

.loader-visual {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 4px solid rgba(0, 125, 83, 0.25);
  border-radius: 50%;
  animation: pulseRing 1.8s cubic-bezier(0.215, 0.610, 0.355, 1) infinite;
}

.ai-sparkle {
  font-size: 2.2rem;
  z-index: 2;
  animation: float 2.5s ease-in-out infinite;
}

.loading-step-item {
  color: #a0aec0;
  font-family: 'Work Sans', sans-serif;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.loading-step-item.active {
  color: #007d53;
  font-weight: 600;
}

.loading-step-item.completed {
  color: #2d3748;
}

.step-check {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #edf2f7;
  font-size: 0.8rem;
  font-weight: bold;
}

.completed .step-check {
  background-color: #e6fffa;
  color: #319795;
}

.active .step-check {
  background-color: rgba(0, 125, 83, 0.1);
  color: #007d53;
}

/* Preview Card */
.preview-card .card-header {
  background-color: #007d53 !important;
  border-bottom: 0;
}

.card-header h4 {
  font-family: 'Work Sans', sans-serif;
}

.font-size-xs {
  font-size: 0.75rem;
}

.font-size-lg {
  font-size: 1.25rem;
}

.badge-soft {
  background-color: #e2e8f0;
  color: #4a5568;
}

.data-source-tag {
  background-color: #edf2f7;
  color: #2d3748;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 6px 12px;
  border-radius: 50px;
  display: inline-flex;
  align-items: center;
  font-family: 'Work Sans', sans-serif;
}

.data-source-tag .bullet {
  width: 8px;
  height: 8px;
  background-color: #3182ce;
  border-radius: 50%;
  margin-right: 8px;
}

.alert-soft-success {
  background-color: #f0fdf4;
  border-left: 4px solid #38a169;
}

.alert-soft-warning {
  background-color: #fffaf0;
  border-left: 4px solid #dd6b20;
}

.btn-deploy {
  background-color: #007d53;
  border-color: #007d53;
  font-family: 'Work Sans', sans-serif;
  font-size: 0.95rem;
  transition: all 0.2s ease;
}

.btn-deploy:hover {
  background-color: #00603f;
  border-color: #00603f;
}

/* Success Modal Details */
.success-icon {
  width: 60px;
  height: 60px;
  background-color: #e6fffa;
  color: #319795;
  font-size: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-weight: bold;
}

/* Custom Multiselect styles overrides for brand compatibility */
.custom-multiselect >>> .multiselect__tag {
  background: #007d53;
}
.custom-multiselect >>> .multiselect__option--highlight {
  background: #007d53;
}

/* Animations */
@keyframes pulseRing {
  0% {
    transform: scale(0.65);
    opacity: 1;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

@keyframes float {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
  100% {
    transform: translateY(0);
  }
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter {
  opacity: 0;
  transform: translateY(10px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Selected Tags Container & Pills */
.selected-tags-pills {
  background-color: #f8f9fa;
  border: 1px solid #e2e8f0;
}

.badge-tag-pill {
  background-color: #007d53;
  color: #ffffff;
  padding: 6px 12px;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: background-color 0.2s ease;
}

.badge-tag-pill:hover {
  background-color: #c53030;
}

.remove-pill {
  margin-left: 6px;
  font-weight: bold;
  font-size: 0.95rem;
}

/* Scrollable Buttons Container */
.tags-buttons-container {
  max-height: 260px;
  overflow-y: auto;
  background-color: #ffffff;
  border: 1px solid #cbd5e0;
}

.btn-tag-selectable {
  border: 1px solid #cbd5e0;
  background-color: #ffffff;
  color: #4a5568;
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 0.78rem;
  font-weight: 500;
  transition: all 0.2s ease;
  font-family: 'Work Sans', sans-serif;
}

.btn-tag-selectable:hover {
  border-color: #007d53;
  color: #007d53;
  background-color: #f0fdf4;
}

.btn-tag-selectable.active {
  background-color: #007d53;
  border-color: #007d53;
  color: #ffffff;
}

.search-input {
  border: 1px solid #cbd5e0;
}

.manual-selection-section {
  background-color: #f8fafc !important;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.02);
}
</style>
